/**
 * T1106 · Platform slot 4 · AppState — web implementation.
 *
 * Composes the four lifecycle event sources the spec identifies (file 05
 * §1464, §1513, §1514):
 *
 *   - `visibilitychange` → `'visible'` / `'hidden'`
 *   - `pageshow` with `persisted === true` → `'bfcache-restore'`
 *   - `freeze` → `'freeze'`
 *   - `resume` → `'resume'` with `ageMs` (= now − freezeAt)
 *   - `pagehide` → `addPageHideListener(persisted)` (unload seam)
 *
 * The `window` / `document` references are injected so this module
 * compiles SSR-safely and unit tests can drive listeners
 * deterministically.
 */

import type { AppStateEvent, AppStateListener, AppStateSlot } from '../adapter.js';

export interface DocumentLike {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange' | 'freeze' | 'resume', listener: () => void): void;
  removeEventListener(type: 'visibilitychange' | 'freeze' | 'resume', listener: () => void): void;
}

export interface PageTransitionEventLike {
  readonly persisted: boolean;
}

export interface WindowLike {
  addEventListener(
    type: 'pageshow' | 'pagehide',
    listener: (ev: PageTransitionEventLike) => void,
  ): void;
  removeEventListener(
    type: 'pageshow' | 'pagehide',
    listener: (ev: PageTransitionEventLike) => void,
  ): void;
}

export interface WebAppStateDeps {
  readonly document: DocumentLike;
  readonly window: WindowLike;
  readonly now?: () => number;
}

export function createWebAppStateSlot(deps: WebAppStateDeps): AppStateSlot {
  const now = deps.now ?? ((): number => Date.now());

  return {
    addListener(cb: AppStateListener): () => void {
      let freezeAt: number | null = null;

      const onVisibility = (): void => {
        const ev: AppStateEvent =
          deps.document.visibilityState === 'visible' ? { kind: 'visible' } : { kind: 'hidden' };
        cb(ev);
      };
      const onPageShow = (ev: PageTransitionEventLike): void => {
        if (ev.persisted === true) cb({ kind: 'bfcache-restore' });
      };
      const onFreeze = (): void => {
        freezeAt = now();
        cb({ kind: 'freeze' });
      };
      const onResume = (): void => {
        const ageMs = freezeAt === null ? 0 : now() - freezeAt;
        freezeAt = null;
        cb({ kind: 'resume', ageMs });
      };

      deps.document.addEventListener('visibilitychange', onVisibility);
      deps.window.addEventListener('pageshow', onPageShow);
      deps.document.addEventListener('freeze', onFreeze);
      deps.document.addEventListener('resume', onResume);

      return (): void => {
        deps.document.removeEventListener('visibilitychange', onVisibility);
        deps.window.removeEventListener('pageshow', onPageShow);
        deps.document.removeEventListener('freeze', onFreeze);
        deps.document.removeEventListener('resume', onResume);
      };
    },
    current(): 'active' | 'background' | 'inactive' {
      return deps.document.visibilityState === 'visible' ? 'active' : 'background';
    },
    addPageHideListener(cb: (persisted: boolean) => void): () => void {
      const onPageHide = (ev: PageTransitionEventLike): void => {
        cb(ev.persisted === true);
      };
      deps.window.addEventListener('pagehide', onPageHide);
      return (): void => {
        deps.window.removeEventListener('pagehide', onPageHide);
      };
    },
  };
}
